// Contextual login header variations based on URL parameters
import { useSearchParams } from 'react-router-dom'
import { UTMLink } from '@/components/custom-ui/utm-link'
import logoReveal from '@/assets/logo_reveal.mp4'

interface LoginHeaderProps {
  className?: string
}

export function LoginHeader({ className }: LoginHeaderProps) {
  const [searchParams] = useSearchParams()
  const flow = searchParams.get('flow')
  const token = searchParams.get('token')

  const renderContent = () => {
    // If there's a token, this is an invitation flow - use more compact styling
    if (token) {
      return (
        <div className="relative">
          {/* Decorative gradient orbs */}
          <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-indigo-200/30 to-transparent rounded-full -translate-y-16 translate-x-16" />
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-gradient-to-tr from-purple-100/40 to-transparent rounded-full translate-y-12 -translate-x-12" />
          
          <div className="relative backdrop-blur-sm p-6">
            <div className="flex flex-col items-center gap-4 text-center">
              <div className="p-3 bg-white/80 backdrop-blur-sm rounded-xl shadow-lg">
                <div className="w-12 h-12 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">👋</span>
                </div>
              </div>
              <div className="space-y-2">
                <h2 className="text-xl font-bold bg-gradient-to-r from-indigo-800 to-purple-700 bg-clip-text text-transparent">
                  Team Invitation
                </h2>
                <p className="text-sm text-slate-600">
                  Sign in or create an account to accept your team invitation.
                </p>
              </div>
            </div>
          </div>
        </div>
      )
    }

    switch (flow) {
      case 'signup':
        return (
          <div className="relative">
            {/* Decorative gradient orbs */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-emerald-200/30 to-transparent rounded-full -translate-y-20 translate-x-20" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-blue-100/40 to-transparent rounded-full translate-y-16 -translate-x-16" />
            
            <div className="relative backdrop-blur-sm p-4 sm:p-6 md:p-8">
              <div className="flex flex-col items-center gap-4 sm:gap-5 md:gap-6 text-center">
                <div className="relative group">
                  <div className="p-2 sm:p-3 md:p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <video 
                      autoPlay 
                      muted 
                      playsInline 
                      className="w-24 h-24 sm:w-32 sm:h-32 md:w-48 md:h-48 rounded-xl transform group-hover:scale-105 transition-transform duration-500"
                    >
                      <source src={logoReveal} type="video/mp4" />
                    </video>
                  </div>
                </div>
                
                <div className="space-y-2 sm:space-y-3 md:space-y-4">
                  <h1 className="text-2xl font-bold tracking-tight sm:text-4xl md:text-6xl bg-gradient-to-r from-emerald-700 via-blue-700 to-indigo-700 bg-clip-text text-transparent">
                    Welcome to Rainfactory
                  </h1>
                  <p className="text-sm sm:text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
                    Join thousands of businesses using Rainfactory's AI-powered marketing platform to transform their brand presence.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )

      case 'pricing':
        return (
          <div className="relative">
            {/* Decorative gradient orbs */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-amber-200/30 to-transparent rounded-full -translate-y-20 translate-x-20" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-orange-100/40 to-transparent rounded-full translate-y-16 -translate-x-16" />
            
            <div className="relative backdrop-blur-sm p-4 sm:p-6 md:p-8">
              <div className="flex flex-col items-center gap-4 sm:gap-5 md:gap-6 text-center">
                <div className="relative group">
                  <div className="p-2 sm:p-3 md:p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <video 
                      autoPlay 
                      muted 
                      playsInline 
                      className="w-24 h-24 sm:w-32 sm:h-32 md:w-48 md:h-48 rounded-xl transform group-hover:scale-105 transition-transform duration-500"
                    >
                      <source src={logoReveal} type="video/mp4" />
                    </video>
                  </div>
                </div>
                
                <div className="space-y-2 sm:space-y-3 md:space-y-4">
                  <h1 className="text-2xl font-bold tracking-tight sm:text-4xl md:text-6xl bg-gradient-to-r from-amber-700 via-orange-700 to-red-600 bg-clip-text text-transparent">
                    Choose Your Plan
                  </h1>
                  <p className="text-sm sm:text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
                    Select the perfect plan for your business needs and unlock the full potential of AI-powered marketing.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )
      
      case 'brand-claim':
        return (
          <div className="relative">
            {/* Decorative gradient orbs */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-purple-200/30 to-transparent rounded-full -translate-y-20 translate-x-20" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-indigo-100/40 to-transparent rounded-full translate-y-16 -translate-x-16" />
            
            <div className="relative backdrop-blur-sm p-4 sm:p-6 md:p-8">
              <div className="flex flex-col items-center gap-4 sm:gap-5 md:gap-6 text-center">
                <div className="relative group">
                  <div className="p-2 sm:p-3 md:p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <video 
                      autoPlay 
                      muted 
                      playsInline 
                      className="w-24 h-24 sm:w-32 sm:h-32 md:w-48 md:h-48 rounded-xl transform group-hover:scale-105 transition-transform duration-500"
                    >
                      <source src={logoReveal} type="video/mp4" />
                    </video>
                  </div>
                </div>
                
                <div className="space-y-2 sm:space-y-3 md:space-y-4">
                  <h1 className="text-2xl font-bold tracking-tight sm:text-4xl md:text-6xl bg-gradient-to-r from-purple-700 via-indigo-700 to-blue-700 bg-clip-text text-transparent">
                    Start your 14-day free trial
                  </h1>
                  <p className="text-sm sm:text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
                    Before continuing, we need to create an account to save your work. You can sign up with Google or email.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )

      default:
        return (
          <div className="relative">
            {/* Decorative gradient orbs */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-blue-200/30 to-transparent rounded-full -translate-y-20 translate-x-20" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-gradient-to-tr from-indigo-100/40 to-transparent rounded-full translate-y-16 -translate-x-16" />
            
            <div className="relative backdrop-blur-sm p-4 sm:p-6 md:p-8">
              <div className="flex flex-col items-center gap-4 sm:gap-5 md:gap-6 text-center">
                <div className="relative group">
                  <div className="p-2 sm:p-3 md:p-4 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg group-hover:shadow-xl transition-all duration-300">
                    <video 
                      autoPlay 
                      muted 
                      playsInline 
                      className="w-24 h-24 sm:w-32 sm:h-32 md:w-48 md:h-48 rounded-xl transform group-hover:scale-105 transition-transform duration-500"
                    >
                      <source src={logoReveal} type="video/mp4" />
                    </video>
                  </div>
                </div>
                
                <div className="space-y-2 sm:space-y-3 md:space-y-4">
                  <h1 className="text-2xl font-bold tracking-tight sm:text-4xl md:text-6xl bg-gradient-to-r from-blue-700 via-indigo-700 to-purple-700 bg-clip-text text-transparent">
                    Welcome Back
                  </h1>
                  <p className="text-sm sm:text-lg md:text-xl text-slate-600 leading-relaxed max-w-2xl">
                    Sign in to continue to your account and unlock the power of AI-driven marketing.
                  </p>
                  <div className="pt-2">
                    <UTMLink 
                      to="/" 
                      className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-blue-600 transition-all duration-300 hover:bg-white/60 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/50 shadow-sm hover:shadow-md transform hover:scale-105"
                    >
                      ← Back to rainfactory.ai
                    </UTMLink>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
    }
  }

  return (
    <div className={`min-h-[20vh] bg-gradient-to-br from-slate-50 via-white to-blue-50/30 relative overflow-hidden ${className || ''}`}>
      {/* Background decorative elements */}
      <div className="absolute inset-0 bg-gradient-to-r from-blue-50/30 via-transparent to-purple-50/30" />
      
      <div className={`container relative z-10 ${token ? 'py-4 sm:py-6 md:py-8' : 'py-6 sm:py-8 md:py-12 lg:py-16'}`}>
        {renderContent()}
      </div>
    </div>
  )
} 